"use client";

import { useEffect, useRef, useState } from "react";

function getSecondsUntilMidnight(): number {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return Math.floor((midnight.getTime() - now.getTime()) / 1000);
}

export type PrimeDayTime = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export function splitSeconds(totalSeconds: number): PrimeDayTime {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

/**
 * Counts down to midnight, then resets to a full 24h cycle.
 * Built on top of the same interval pattern as useCountdown but adds auto-reset.
 */
export function usePrimeDayCountdown(): PrimeDayTime {
  const [seconds, setSeconds] = useState(getSecondsUntilMidnight);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) return 86400;
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return splitSeconds(seconds);
}
